import { useState, useCallback, useEffect, useRef } from 'react'
import axios from 'axios'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { API_BASE_URL } from '../constants/system'
import { CarbonSpec } from '../types/carbon/models/CarbonSpec'
import { BatchTablePreview } from '../components/batch/BatchConfirmDialog'
import { PlatinumProperties } from '../types/carbon/models/PlatinumProperties'
import { andFilters } from '../utils/filter'
import { extractErrorMessage } from '../utils/apitools'

export interface SourceTable {
  name: string
  path: string
  relativePath: string
  spec: CarbonSpec
  description: string
}

export type OverrideMode = 'keep' | 'replace' | 'remove'
export type FilterOverrideMode = 'keep' | 'replace' | 'append' | 'remove'

export type CellsMode = 'keep' | 'set'

export interface BatchCells {
  frequencies: boolean
  columnPercents: boolean
  rowPercents: boolean
}

export interface BatchDecimals {
  frequencies: number
  percents: number
}

const DEFAULT_CELLS: BatchCells = { frequencies: true, columnPercents: true, rowPercents: false }
const DEFAULT_DECIMALS: BatchDecimals = { frequencies: 0, percents: 2 }

/** Tables per folder-specs request while loading a folder (#1809). */
export const FOLDER_SPECS_PAGE_SIZE = 200

export const isValidDecimal = (n: number): boolean => Number.isInteger(n) && n >= 0 && n <= 9

export interface UseBatchRegenerateProps {
  customer: string
  job: string
  sourceFolder: string
  /** The signed-in user's email: overwrite is offered only inside their own folder. */
  userEmail?: string
}

export interface BatchFailedTable {
  name: string
  error: string
}

/**
 * The folder inside the user's own My Tables that `sourceFolder` (a full blob
 * path) names, e.g. "Sky/Wave1" for "Tables/User/<email>/Sky/Wave1". Null for
 * Exec folders, a colleague's folder, or the user's root. The server applies
 * the same rule (overwriteTargetFolder in tables_batch_generate.go) and is
 * the one that enforces it.
 */
export const ownFolderPath = (sourceFolder: string, userEmail: string | undefined): string | null => {
  if (!userEmail) return null
  const prefix = `tables/user/${userEmail.toLowerCase()}/`
  if (!sourceFolder.toLowerCase().startsWith(prefix)) return null
  const rest = sourceFolder.slice(prefix.length).replace(/\/+$/, '')
  return rest || null
}

/** Tables per batch-generate request: Go sends the folder in parts so the
 *  page can count them as they are saved (#1809). */
export const REGENERATE_PART_SIZE = 25

/** A weight as Carbon takes it: the variable name and empty brackets,
 *  `WeightCombo()`, never codes (Matt, #1809). */
export const toWeightSyntax = (value: string): string => {
  const name = value.trim().split('(')[0].trim()
  // One variable name only: a typed expression (w1*w2) is refused, not mangled.
  return /^[A-Za-z_][A-Za-z0-9_.]*$/.test(name) ? `${name}()` : ''
}

/** Whether a run gave any count at all: a case filter no case passes gives
 *  a table of zeros, not an error. */
export const hasAnyCount = (data: { cells?: { rows?: { cell?: { freq?: number | null }[] }[] } } | null | undefined): boolean =>
  (data?.cells?.rows || []).some(r => (r.cell || []).some(c => typeof c.freq === 'number' && c.freq !== 0))

/** Progress of a regenerate after Go: tables saved or refused so far. */
export interface RegenerateProgress {
  done: number
  total: number
}

export interface RegenerateResult {
  count: number
  failed: BatchFailedTable[]
  /** Why the regenerate stopped before the end, when it did. */
  error?: string
}

/** A part the server answered with 400 because it saved none of its tables
 *  ("no tables were generated", each with its reason): a refusal per table,
 *  not a reason to stop. */
const refusedPart = (err: unknown): BatchFailedTable[] | null => {
  const res = (err as { response?: { status?: number; data?: { failed?: BatchFailedTable[] } } }).response
  return res?.status === 400 && Array.isArray(res.data?.failed) && res.data.failed.length > 0 ? res.data.failed : null
}

/** One line naming what the server refused, e.g. "Q1: already exists — …". */
export const describeFailedTables = (failed: BatchFailedTable[] | undefined): string => {
  if (!failed || failed.length === 0) return ''
  const first = `${failed[0].name}: ${failed[0].error}`
  return failed.length === 1 ? first : `${first} (and ${failed.length - 1} more)`
}

export const useBatchRegenerate = ({ customer, job, sourceFolder, userEmail }: UseBatchRegenerateProps) => {
  const [sourceTables, setSourceTables] = useState<SourceTable[]>([])
  const [loadingSource, setLoadingSource] = useState(false)
  const [sourceError, setSourceError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const loadRequestRef = useRef(0)

  const [topMode, setTopMode] = useState<OverrideMode>('keep')
  const [topValue, setTopValue] = useState('')
  const [filterMode, setFilterMode] = useState<FilterOverrideMode>('keep')
  const [filterValue, setFilterValue] = useState('')
  const [weightMode, setWeightMode] = useState<OverrideMode>('keep')
  const [weightValue, setWeightValueRaw] = useState('')
  const [caseFilterMode, setCaseFilterMode] = useState<OverrideMode>('keep')
  const [caseFilterValue, setCaseFilterValue] = useState('')

  const [prefix, setPrefix] = useState('')
  const [suffix, setSuffix] = useState('')
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState<RegenerateProgress | null>(null)
  const [overwriteRequested, setOverwrite] = useState(false)

  const setWeightValue = useCallback((value: string) => setWeightValueRaw(toWeightSyntax(value)), [])

  // Cell bands and decimals: the user's edits sit over a starting value read
  // from the first source table, so nothing is copied into state.
  const [cellsMode, setCellsMode] = useState<CellsMode>('keep')
  const [cellsEdits, setCellsEdits] = useState<Partial<BatchCells>>({})
  const [decimalsMode, setDecimalsMode] = useState<CellsMode>('keep')
  const [decimalsEdits, setDecimalsEdits] = useState<Partial<BatchDecimals>>({})

  const queryClient = useQueryClient()

  const firstDisplay = sourceTables[0]?.spec.displayProperties
  const cells: BatchCells = {
    frequencies: cellsEdits.frequencies ?? firstDisplay?.cells?.frequencies ?? DEFAULT_CELLS.frequencies,
    columnPercents: cellsEdits.columnPercents ?? firstDisplay?.cells?.columnPercents ?? DEFAULT_CELLS.columnPercents,
    rowPercents: cellsEdits.rowPercents ?? firstDisplay?.cells?.rowPercents ?? DEFAULT_CELLS.rowPercents,
  }
  const decimals: BatchDecimals = {
    frequencies: decimalsEdits.frequencies ?? firstDisplay?.decimals?.frequencies ?? DEFAULT_DECIMALS.frequencies,
    percents: decimalsEdits.percents ?? firstDisplay?.decimals?.percents ?? DEFAULT_DECIMALS.percents,
  }
  const cellsInvalid = cellsMode === 'set' && !cells.frequencies && !cells.columnPercents && !cells.rowPercents
  const decimalsInvalid = decimalsMode === 'set' && !(isValidDecimal(decimals.frequencies) && isValidDecimal(decimals.percents))

  const setCells = useCallback((patch: Partial<BatchCells>) => setCellsEdits(prev => ({ ...prev, ...patch })), [])
  const setDecimals = useCallback((patch: Partial<BatchDecimals>) => setDecimalsEdits(prev => ({ ...prev, ...patch })), [])

  const ownFolder = ownFolderPath(sourceFolder, userEmail)
  const canOverwrite = ownFolder !== null
  const overwrite = overwriteRequested && canOverwrite

  const sourceFolderLeaf = sourceFolder.split('/').pop() || 'Regenerated'
  // Overwrite writes back into the source folder (nested path kept); a new
  // regenerate goes to a prefixed copy of the leaf folder.
  const destinationFolder = overwrite && ownFolder
    ? ownFolder
    : prefix ? `${prefix}${sourceFolderLeaf}` : sourceFolderLeaf

  const newTableName = useCallback(
    (name: string) => overwrite ? name : `${prefix}${name}${suffix}`,
    [overwrite, prefix, suffix],
  )

  // Load source tables page by page, so a big folder shows a running count
  // (#1809). A page that answers after the folder changed is dropped.
  useEffect(() => {
    if (!customer || !job || !sourceFolder) return
    const requestId = ++loadRequestRef.current
    const isCurrent = () => loadRequestRef.current === requestId
    setLoadingSource(true)
    setSourceError(null)
    setSourceTables([])
    setTotalCount(0)

    const loadPages = async () => {
      let offset = 0
      for (;;) {
        const res = await axios.get<{ tables: SourceTable[]; total?: number }>(
          `${API_BASE_URL}/tables/${customer}/${job}/folder-specs`,
          { params: { folderPath: sourceFolder, recursive: 'true', offset, limit: FOLDER_SPECS_PAGE_SIZE } }
        )
        if (!isCurrent()) return
        const page = res.data.tables || []
        const total = res.data.total
        setSourceTables(prev => [...prev, ...page])
        setTotalCount(total ?? page.length)
        offset += FOLDER_SPECS_PAGE_SIZE
        // A server without paging sends every table and no total.
        if (total === undefined || page.length === 0 || offset >= total) return
      }
    }

    loadPages().catch(err => {
      if (isCurrent()) setSourceError(`Failed to load source tables: ${(err as Error).message}`)
    }).finally(() => {
      if (isCurrent()) setLoadingSource(false)
    })
  }, [customer, job, sourceFolder])

  // A new case filter is run once on the first table. Carbon counts a case
  // only when its value in the case filter variable is one code, so a
  // variable that gives a case several codes (bb_age on Prius) passes no
  // case and every table comes out 0 (#1809). Say so before Go.
  const firstTable = sourceTables[0]
  const newCaseFilter = caseFilterMode === 'replace' ? caseFilterValue.trim() : ''
  const caseFilterCheck = useQuery({
    queryKey: ['regenerate-case-filter-check', customer, job, firstTable?.path, newCaseFilter],
    enabled: Boolean(newCaseFilter && firstTable && customer && job),
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      const spec = firstTable.spec
      const res = await axios.post(`${API_BASE_URL}/tables/${customer}/${job}/run`, {
        ...spec,
        caseFilter: newCaseFilter,
        specProperties: { ...spec.specProperties, caseFilter: newCaseFilter },
      })
      return hasAnyCount(res.data)
    },
  })
  /** The first table, run with the new case filter, has no cases. */
  const caseFilterEmpty = Boolean(newCaseFilter) && caseFilterCheck.data === false
  const caseFilterChecking = Boolean(newCaseFilter) && caseFilterCheck.isFetching

  const resolveTop = useCallback((original: string) => {
    if (topMode === 'replace') return topValue
    if (topMode === 'remove') return ''
    return original
  }, [topMode, topValue])

  const resolveFilter = useCallback((original: string) => {
    if (filterMode === 'replace') return filterValue
    if (filterMode === 'remove') return ''
    if (filterMode === 'append') return andFilters(original, filterValue)
    return original
  }, [filterMode, filterValue])

  const resolveWeight = useCallback((original: string) => {
    if (weightMode === 'replace') return weightValue
    if (weightMode === 'remove') return ''
    return original
  }, [weightMode, weightValue])

  const resolveCaseFilter = useCallback((original: string) => {
    if (caseFilterMode === 'replace') return caseFilterValue
    if (caseFilterMode === 'remove') return ''
    return original
  }, [caseFilterMode, caseFilterValue])

  const buildTablePreviews = useCallback((): BatchTablePreview[] => {
    return sourceTables.map(t => ({
      name: newTableName(t.name),
      variable: t.name,
      description: t.description || '',
      folder: t.relativePath || '',
    }))
  }, [sourceTables, newTableName])

  /**
   * Go: sends the tables in parts of REGENERATE_PART_SIZE, one after another,
   * and counts them as they are saved. Each part carries the same folder,
   * overwrite and source settings, and the server checks every table on its
   * own, so "already exists" holds across parts. Carbon's cache is cleared on
   * the last part only, and cached tables are dropped once at the end.
   */
  const submit = useCallback(async (): Promise<RegenerateResult> => {
    // Each entry carries its own resolved top/filter/weight/caseFilter plus the
    // source table's full display + spec properties, so the backend can
    // round-trip per-table settings (decimals, cell visibility, InitAsMissing,
    // etc.) instead of falling back to hardcoded defaults.
    const tables = sourceTables.map(t => {
      const resolvedTop = resolveTop(t.spec.top || '')
      const resolvedFilter = resolveFilter(t.spec.filter || '')
      const resolvedWeight = resolveWeight(t.spec.weight || '')
      const resolvedCaseFilter = resolveCaseFilter(t.spec.caseFilter || '')

      let displayProperties: PlatinumProperties | undefined = t.spec.displayProperties
      if (displayProperties?.significance) {
        displayProperties = {
          ...displayProperties,
          significance: {
            ...displayProperties.significance,
            visible: false,
            statsParam: '',
          },
        }
      }

      return {
        name: newTableName(t.name),
        side: t.spec.side || '',
        description: t.description || '',
        subFolder: t.relativePath || '',
        top: resolvedTop,
        filter: resolvedFilter,
        weight: resolvedWeight,
        caseFilter: resolvedCaseFilter,
        useFilter: !!resolvedFilter,
        useWeight: !!resolvedWeight,
        displayProperties,
        specProperties: t.spec.specProperties,
      }
    })

    const total = tables.length
    let count = 0
    let done = 0
    const failed: BatchFailedTable[] = []
    let error: string | undefined
    setLoading(true)
    setProgress({ done: 0, total })
    try {
      for (let start = 0; start < total; start += REGENERATE_PART_SIZE) {
        const part = tables.slice(start, start + REGENERATE_PART_SIZE)
        const last = start + REGENERATE_PART_SIZE >= total
        try {
          const res = await axios.post<{ count: number; failed?: BatchFailedTable[] }>(
            `${API_BASE_URL}/tables/${customer}/${job}/batch-generate`,
            {
              tables: part,
              destination: 'user',
              execFolder: destinationFolder,
              isRegenerate: true,
              overwrite,
              sourceFolder,
              skipCacheEvict: !last,
              ...(cellsMode === 'set' ? { cells } : {}),
              ...(decimalsMode === 'set' ? { decimals } : {}),
            },
          )
          count += res.data.count
          failed.push(...(res.data.failed || []))
        } catch (err) {
          const refused = refusedPart(err)
          if (!refused) {
            error = extractErrorMessage(err)
            break
          }
          failed.push(...refused)
        }
        done += part.length
        setProgress({ done, total })
      }
    } finally {
      // An overwrite changes tables the Tables page may already hold in its
      // cache; drop them so the next open fetches the new result.
      if (overwrite && count > 0) queryClient.removeQueries({ queryKey: ['table'] })
      setLoading(false)
      setProgress(null)
    }
    return { count, failed, ...(error ? { error } : {}) }
  }, [customer, job, sourceTables, newTableName, destinationFolder, overwrite, sourceFolder,
      resolveTop, resolveFilter, resolveWeight, resolveCaseFilter,
      cellsMode, cells, decimalsMode, decimals, queryClient])

  return {
    sourceTables, loadingSource, sourceError,
    loadedCount: sourceTables.length, totalCount,
    topMode, setTopMode, topValue, setTopValue,
    filterMode, setFilterMode, filterValue, setFilterValue,
    weightMode, setWeightMode, weightValue, setWeightValue,
    caseFilterMode, setCaseFilterMode, caseFilterValue, setCaseFilterValue,
    cellsMode, setCellsMode, cells, setCells, cellsInvalid,
    decimalsMode, setDecimalsMode, decimals, setDecimals, decimalsInvalid,
    prefix, setPrefix,
    suffix, setSuffix,
    canOverwrite, overwrite, setOverwrite,
    newTableName,
    destinationFolder,
    loading,
    progress,
    caseFilterEmpty,
    caseFilterChecking,
    buildTablePreviews,
    submit,
    resolveTop, resolveFilter, resolveWeight, resolveCaseFilter,
  }
}

export default useBatchRegenerate
